/**
 * SchoolAccessPanel — school-level counsellor accounts panel (Tier 2).
 *
 * Three states, all rendered inside the same card:
 *   • Not in a school → Create + Join forms side by side
 *   • Member → shows school name, member roster, "Leave school" button
 *   • Admin → shows invite code (copyable + rotatable) + roster + a
 *     read-only rollup of every colleague's cohorts.
 *
 * Backed by:
 *   GET  /counsellors/school
 *   POST /counsellors/school/create   { name }
 *   POST /counsellors/school/join     { invite_code }
 *   POST /counsellors/school/leave
 *   POST /counsellors/school/rotate-invite
 *   GET  /counsellors/school/cohorts
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, Building2, KeyRound, LogOut, RefreshCw, Copy, ClipboardCheck, Shield, Link as LinkIcon } from "lucide-react";
import SchoolInsightsPanel from "./SchoolInsightsPanel";

/**
 * Reads a school-invite `?code=` from the URL (used by the
 * `/counsellors/join?code=XXXX` share-link route) so a colleague
 * landing here has the join form pre-filled. Cleared from the URL
 * after read so a refresh doesn't retry.
 */
function useInviteCodeFromUrl() {
  const [code, setCode] = useState("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const c = (params.get("code") || "").trim().toUpperCase();
    if (c) {
      setCode(c);
      // Strip the code param so a page refresh doesn't retry the
      // join — but keep any other params intact.
      params.delete("code");
      const q = params.toString();
      const clean = window.location.pathname + (q ? `?${q}` : "");
      window.history.replaceState({}, "", clean);
    }
  }, []);
  return code;
}

function InviteCodePill({ code, onRotate }) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/counsellors/join?code=${code}`
    : `/counsellors/join?code=${code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Could not copy invite code");
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      toast.success("Share link copied — paste it to a colleague and they'll accept in one tap");
      setTimeout(() => setLinkCopied(false), 1400);
    } catch {
      toast.error("Could not copy share link");
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        data-testid="school-invite-code-copy"
        onClick={copy}
        className="inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 font-mono text-sm tracking-widest text-emerald-900 hover:bg-emerald-100"
        title="Copy invite code — colleagues use this to join your school"
      >
        <KeyRound className="w-3.5 h-3.5" />
        <span data-testid="school-invite-code">{code}</span>
        {copied ? <ClipboardCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        data-testid="school-invite-share-link"
        onClick={copyLink}
        title="Copy a share link — colleagues open the link and the join form is pre-filled"
        className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2.5 py-1.5 text-xs text-emerald-800 hover:bg-emerald-50"
      >
        {linkCopied ? <ClipboardCheck className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}
        Copy share link
      </button>
      <button
        type="button"
        data-testid="school-invite-rotate"
        onClick={onRotate}
        title="Rotate the code — the old one stops working immediately"
        className="inline-flex items-center gap-1 rounded-md border border-stone-300 px-2.5 py-1.5 text-xs text-stone-600 hover:bg-stone-50"
      >
        <RefreshCw className="w-3 h-3" /> Rotate
      </button>
    </div>
  );
}

function CreateJoinForms({ onDone, prefillCode = "" }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState(prefillCode);
  const [busy, setBusy] = useState("");

  // Autofocus the join input and light it up briefly when a share-link
  // pre-fills the code so the colleague immediately sees where to tap.
  useEffect(() => {
    if (!prefillCode) return;
    setCode(prefillCode);
    const el = document.querySelector('[data-testid="school-join-code"]');
    if (el) {
      el.focus();
      el.classList.add("ring-2", "ring-emerald-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400"), 2400);
    }
  }, [prefillCode]);

  const create = async () => {
    if (!name.trim()) return toast.error("Give your school a name");
    setBusy("create");
    try {
      await api.post("/counsellors/school/create", { name: name.trim() });
      toast.success("School created — share the invite code with colleagues");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create school");
    } finally { setBusy(""); }
  };
  const join = async () => {
    if (!code.trim()) return toast.error("Paste the invite code your admin sent");
    setBusy("join");
    try {
      await api.post("/counsellors/school/join", { invite_code: code.trim().toUpperCase() });
      toast.success("Joined the school — you'll now see cohorts from all colleagues");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join school");
    } finally { setBusy(""); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="rounded-md border border-stone-200 bg-stone-50 p-4" data-testid="school-create-form">
        <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
          <Building2 className="w-4 h-4" /> Start a school
        </div>
        <p className="text-[12px] text-stone-500 mt-1">
          You become the admin. Colleagues join with a code you share.
        </p>
        <div className="mt-3 space-y-2">
          <Input
            data-testid="school-create-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="St Paul's Sixth Form"
          />
          <Button
            data-testid="school-create-submit"
            onClick={create}
            disabled={busy === "create"}
            className="w-full"
          >
            {busy === "create" ? "Creating…" : "Create school"}
          </Button>
        </div>
      </div>
      <div className="rounded-md border border-stone-200 bg-stone-50 p-4" data-testid="school-join-form">
        <div className="flex items-center gap-2 text-sm font-semibold text-stone-800">
          <KeyRound className="w-4 h-4" /> Join with an invite code
        </div>
        <p className="text-[12px] text-stone-500 mt-1">
          Get the 8-character code from your school admin.
        </p>
        <div className="mt-3 space-y-2">
          <Input
            data-testid="school-join-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABCD2345"
            maxLength={8}
            className="font-mono tracking-widest uppercase"
          />
          <Button
            data-testid="school-join-submit"
            onClick={join}
            disabled={busy === "join"}
            variant="outline"
            className="w-full"
          >
            {busy === "join" ? "Joining…" : "Join school"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SchoolCohortsList({ userId }) {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/counsellors/school/cohorts");
      setRows(r.data.results || []);
    } catch {
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);
  if (rows === null) {
    return <div className="mt-4 text-xs text-stone-500">Loading colleague cohorts…</div>;
  }
  if (!rows.length) {
    return (
      <div className="mt-4 text-xs text-stone-500" data-testid="school-cohorts-empty">
        No cohorts yet — once colleagues create cohorts they'll show here.
      </div>
    );
  }
  return (
    <div className="mt-4" data-testid="school-cohorts-list">
      <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mb-2">
        All cohorts across the school ({rows.length})
      </div>
      <ul className="divide-y divide-stone-200 border border-stone-200 rounded-md bg-white">
        {rows.map((c) => (
          <li key={c.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`school-cohort-row-${c.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-stone-800 truncate">{c.name}</div>
              <div className="text-[11px] text-stone-500 truncate">
                {c.counsellor_role || "Counsellor"} · {c.academic_year}
                {c.is_mine && <span className="ml-2 text-emerald-700">· yours</span>}
              </div>
            </div>
            <div className="text-[11px] font-mono text-stone-600 whitespace-nowrap ml-4">
              {c.student_count} student{c.student_count === 1 ? "" : "s"}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SchoolAccessPanel({ me, onMeChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const inviteCodeFromUrl = useInviteCodeFromUrl();

  // If a share-link invite arrives while the user is already in a
  // school, we can't silently switch them over — tell them clearly.
  useEffect(() => {
    if (inviteCodeFromUrl && me?.school) {
      toast.info(
        `You're already in ${me.school.name}. Leave first, then re-open the invite link to join a different school.`
      );
    }
  }, [inviteCodeFromUrl, me?.school]);

  const load = async () => {
    if (!me?.school) { setDetail(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/counsellors/school");
      setDetail(r.data);
    } catch {
      setDetail(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [me?.school?.id]);

  const refresh = async () => {
    try {
      const r = await api.get("/counsellors/me");
      onMeChanged?.(r.data);
    } catch {}
  };

  const rotate = async () => {
    try {
      const r = await api.post("/counsellors/school/rotate-invite");
      toast.success("Invite code rotated — the old one is no longer valid");
      setDetail((d) => d ? { ...d, school: { ...d.school, invite_code: r.data.school.invite_code } } : d);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rotate invite");
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this school? You'll stop seeing colleague cohorts.")) return;
    try {
      await api.post("/counsellors/school/leave");
      toast.success("Left the school");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not leave school");
    }
  };

  // ---- Not in a school → onboarding forms -------------------------------
  if (!me?.school) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-5" data-testid="school-access-panel">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-stone-500" />
          <div className="text-sm font-semibold text-stone-800">Share this workspace with colleagues</div>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Tier 2 · Beta</Badge>
        </div>
        <p className="text-[12px] text-stone-500 mt-1">
          Roll up every colleague's cohorts under one school — great for a head of sixth
          who needs to pattern-scan the whole year without owning each cohort.
        </p>
        <div className="mt-4">
          <CreateJoinForms onDone={refresh} prefillCode={inviteCodeFromUrl} />
        </div>
      </div>
    );
  }

  // ---- In a school → members + (admin: invite + rotate) + rollup --------
  const isAdmin = !!me.school.is_admin;
  const members = detail?.members || [];
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-5" data-testid="school-access-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-800" />
            <div className="text-sm font-semibold text-stone-800" data-testid="school-name">
              {me.school.name}
            </div>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider bg-white">
              {isAdmin ? <span className="flex items-center gap-1"><Shield className="w-3 h-3" />Admin</span> : "Member"}
            </Badge>
          </div>
          <p className="text-[12px] text-stone-500 mt-1">
            {members.length} member{members.length === 1 ? "" : "s"} · every colleague's
            cohorts show below (read-only)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && detail?.school?.invite_code && (
            <InviteCodePill code={detail.school.invite_code} onRotate={rotate} />
          )}
          <Button
            data-testid="school-leave-btn"
            variant="outline"
            size="sm"
            onClick={leave}
            className="text-stone-700"
          >
            <LogOut className="w-3 h-3 mr-1" /> Leave
          </Button>
        </div>
      </div>

      {members.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500 mb-2">
            Members
          </div>
          <ul className="flex flex-wrap gap-2" data-testid="school-members-list">
            {members.map((m) => (
              <li
                key={m.user_id}
                className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] text-stone-700"
                data-testid={`school-member-${m.user_id}`}
              >
                <span className="font-medium">{m.role || "Counsellor"}</span>
                {m.is_school_admin && <Shield className="w-3 h-3 text-emerald-700" />}
              </li>
            ))}
          </ul>
        </div>
      )}

      <SchoolCohortsList userId={me.user_id} />
      <SchoolInsightsPanel />
      {loading && <div className="mt-2 text-[11px] text-stone-400">Refreshing…</div>}
    </div>
  );
}
